/**
 * Single source of truth for jurisdictions + countries.
 *
 * Conventions (frozen 2026-08-01, post-cleanup):
 *   - JURISDICTIONS: 8 1Financial operating jurisdictions (short codes).
 *   - COUNTRIES: 249 ISO 3166-1 alpha-2 codes with human label + flag-icons CSS class.
 *   - flagCode: lowercase 2-letter ISO, used with `<span class="fi fi-ae">`.
 *   - NO backward-compat aliases — legacy data is migrated at write-time only.
 *
 * The 8 jurisdictions are NOT a subset of the country list. They are internal
 * operating jurisdictions (e.g. "UAE" is a 1Financial jurisdiction; the
 * corresponding country code in the schema is "AE"). Mappings live in the
 * application layer if needed.
 */

export interface Jurisdiction {
  readonly code: string;
  readonly label: string;
  readonly flagCode: string;
}

export interface Country {
  readonly code: string;
  readonly label: string;
  readonly flagCode: string;
}

export const JURISDICTIONS: readonly Jurisdiction[] = [
  { code: 'UAE',          label: 'UAE',                       flagCode: 'ae' },
  { code: 'UK',           label: 'UK',                        flagCode: 'gb' },
  { code: 'KSA',          label: 'KSA',                       flagCode: 'sa' },
  { code: 'USA',          label: 'USA',                       flagCode: 'us' },
  { code: 'PAK',          label: 'Pakistan',                  flagCode: 'pk' },
  { code: 'Singapore',    label: 'Singapore',                 flagCode: 'sg' },
  { code: 'BVI',          label: 'British Virgin Islands',    flagCode: 'vg' },
  { code: 'Cayman Island',label: 'Cayman Islands',            flagCode: 'ky' },
] as const;

export const JURISDICTION_CODES: readonly string[] = JURISDICTIONS.map((j) => j.code);
export const JURISDICTION_BY_CODE: Readonly<Record<string, Jurisdiction>> = Object.freeze(
  Object.fromEntries(JURISDICTIONS.map((j) => [j.code, j])),
);

export const JURISDICTION_LABELS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(JURISDICTIONS.map((j) => [j.code, j.label])),
);

export const COUNTRIES: readonly Country[] = [
  { code: 'AD', label: 'Andorra',                                  flagCode: 'ad' },
  { code: 'AE', label: 'United Arab Emirates',                      flagCode: 'ae' },
  { code: 'AF', label: 'Afghanistan',                              flagCode: 'af' },
  { code: 'AG', label: 'Antigua and Barbuda',                       flagCode: 'ag' },
  { code: 'AI', label: 'Anguilla',                                 flagCode: 'ai' },
  { code: 'AL', label: 'Albania',                                  flagCode: 'al' },
  { code: 'AM', label: 'Armenia',                                  flagCode: 'am' },
  { code: 'AO', label: 'Angola',                                   flagCode: 'ao' },
  { code: 'AQ', label: 'Antarctica',                               flagCode: 'aq' },
  { code: 'AR', label: 'Argentina',                                flagCode: 'ar' },
  { code: 'AS', label: 'American Samoa',                           flagCode: 'as' },
  { code: 'AT', label: 'Austria',                                  flagCode: 'at' },
  { code: 'AU', label: 'Australia',                                flagCode: 'au' },
  { code: 'AW', label: 'Aruba',                                    flagCode: 'aw' },
  { code: 'AX', label: 'Åland Islands',                            flagCode: 'ax' },
  { code: 'AZ', label: 'Azerbaijan',                               flagCode: 'az' },
  { code: 'BA', label: 'Bosnia and Herzegovina',                   flagCode: 'ba' },
  { code: 'BB', label: 'Barbados',                                 flagCode: 'bb' },
  { code: 'BD', label: 'Bangladesh',                               flagCode: 'bd' },
  { code: 'BE', label: 'Belgium',                                  flagCode: 'be' },
  { code: 'BF', label: 'Burkina Faso',                             flagCode: 'bf' },
  { code: 'BG', label: 'Bulgaria',                                 flagCode: 'bg' },
  { code: 'BH', label: 'Bahrain',                                  flagCode: 'bh' },
  { code: 'BI', label: 'Burundi',                                  flagCode: 'bi' },
  { code: 'BJ', label: 'Benin',                                    flagCode: 'bj' },
  { code: 'BL', label: 'Saint Barthélemy',                         flagCode: 'bl' },
  { code: 'BM', label: 'Bermuda',                                  flagCode: 'bm' },
  { code: 'BN', label: 'Brunei',                                   flagCode: 'bn' },
  { code: 'BO', label: 'Bolivia',                                  flagCode: 'bo' },
  { code: 'BQ', label: 'Bonaire, Sint Eustatius and Saba',         flagCode: 'bq' },
  { code: 'BR', label: 'Brazil',                                   flagCode: 'br' },
  { code: 'BS', label: 'Bahamas',                                  flagCode: 'bs' },
  { code: 'BT', label: 'Bhutan',                                   flagCode: 'bt' },
  { code: 'BV', label: 'Bouvet Island',                            flagCode: 'bv' },
  { code: 'BW', label: 'Botswana',                                 flagCode: 'bw' },
  { code: 'BY', label: 'Belarus',                                  flagCode: 'by' },
  { code: 'BZ', label: 'Belize',                                   flagCode: 'bz' },
  { code: 'CA', label: 'Canada',                                   flagCode: 'ca' },
  { code: 'CC', label: 'Cocos (Keeling) Islands',                  flagCode: 'cc' },
  { code: 'CD', label: 'DR Congo',                                 flagCode: 'cd' },
  { code: 'CF', label: 'Central African Republic',                 flagCode: 'cf' },
  { code: 'CG', label: 'Republic of the Congo',                    flagCode: 'cg' },
  { code: 'CH', label: 'Switzerland',                              flagCode: 'ch' },
  { code: 'CI', label: "Côte d'Ivoire",                            flagCode: 'ci' },
  { code: 'CK', label: 'Cook Islands',                             flagCode: 'ck' },
  { code: 'CL', label: 'Chile',                                    flagCode: 'cl' },
  { code: 'CM', label: 'Cameroon',                                 flagCode: 'cm' },
  { code: 'CN', label: 'China',                                    flagCode: 'cn' },
  { code: 'CO', label: 'Colombia',                                 flagCode: 'co' },
  { code: 'CR', label: 'Costa Rica',                               flagCode: 'cr' },
  { code: 'CU', label: 'Cuba',                                     flagCode: 'cu' },
  { code: 'CV', label: 'Cabo Verde',                               flagCode: 'cv' },
  { code: 'CW', label: 'Curaçao',                                  flagCode: 'cw' },
  { code: 'CX', label: 'Christmas Island',                         flagCode: 'cx' },
  { code: 'CY', label: 'Cyprus',                                   flagCode: 'cy' },
  { code: 'CZ', label: 'Czechia',                                  flagCode: 'cz' },
  { code: 'DE', label: 'Germany',                                  flagCode: 'de' },
  { code: 'DJ', label: 'Djibouti',                                 flagCode: 'dj' },
  { code: 'DK', label: 'Denmark',                                  flagCode: 'dk' },
  { code: 'DM', label: 'Dominica',                                 flagCode: 'dm' },
  { code: 'DZ', label: 'Algeria',                                  flagCode: 'dz' },
  { code: 'DO', label: 'Dominican Republic',                       flagCode: 'do' },
  { code: 'EC', label: 'Ecuador',                                  flagCode: 'ec' },
  { code: 'EE', label: 'Estonia',                                  flagCode: 'ee' },
  { code: 'EG', label: 'Egypt',                                    flagCode: 'eg' },
  { code: 'EH', label: 'Western Sahara',                           flagCode: 'eh' },
  { code: 'ER', label: 'Eritrea',                                  flagCode: 'er' },
  { code: 'ES', label: 'Spain',                                    flagCode: 'es' },
  { code: 'ET', label: 'Ethiopia',                                 flagCode: 'et' },
  { code: 'FI', label: 'Finland',                                  flagCode: 'fi' },
  { code: 'FJ', label: 'Fiji',                                     flagCode: 'fj' },
  { code: 'FK', label: 'Falkland Islands',                         flagCode: 'fk' },
  { code: 'FM', label: 'Micronesia',                               flagCode: 'fm' },
  { code: 'FO', label: 'Faroe Islands',                            flagCode: 'fo' },
  { code: 'FR', label: 'France',                                   flagCode: 'fr' },
  { code: 'GA', label: 'Gabon',                                    flagCode: 'ga' },
  { code: 'GB', label: 'United Kingdom',                           flagCode: 'gb' },
  { code: 'GD', label: 'Grenada',                                  flagCode: 'gd' },
  { code: 'GE', label: 'Georgia',                                  flagCode: 'ge' },
  { code: 'GF', label: 'French Guiana',                            flagCode: 'gf' },
  { code: 'GG', label: 'Guernsey',                                 flagCode: 'gg' },
  { code: 'GH', label: 'Ghana',                                    flagCode: 'gh' },
  { code: 'GI', label: 'Gibraltar',                                flagCode: 'gi' },
  { code: 'GL', label: 'Greenland',                                flagCode: 'gl' },
  { code: 'GM', label: 'Gambia',                                   flagCode: 'gm' },
  { code: 'GN', label: 'Guinea',                                   flagCode: 'gn' },
  { code: 'GP', label: 'Guadeloupe',                               flagCode: 'gp' },
  { code: 'GQ', label: 'Equatorial Guinea',                        flagCode: 'gq' },
  { code: 'GR', label: 'Greece',                                   flagCode: 'gr' },
  { code: 'GS', label: 'South Georgia and the South Sandwich Islands', flagCode: 'gs' },
  { code: 'GT', label: 'Guatemala',                                flagCode: 'gt' },
  { code: 'GU', label: 'Guam',                                     flagCode: 'gu' },
  { code: 'GW', label: 'Guinea-Bissau',                            flagCode: 'gw' },
  { code: 'GY', label: 'Guyana',                                   flagCode: 'gy' },
  { code: 'HK', label: 'Hong Kong',                                flagCode: 'hk' },
  { code: 'HM', label: 'Heard Island and McDonald Islands',        flagCode: 'hm' },
  { code: 'HN', label: 'Honduras',                                 flagCode: 'hn' },
  { code: 'HR', label: 'Croatia',                                  flagCode: 'hr' },
  { code: 'HT', label: 'Haiti',                                    flagCode: 'ht' },
  { code: 'HU', label: 'Hungary',                                  flagCode: 'hu' },
  { code: 'ID', label: 'Indonesia',                                flagCode: 'id' },
  { code: 'IE', label: 'Ireland',                                  flagCode: 'ie' },
  { code: 'IL', label: 'Israel',                                   flagCode: 'il' },
  { code: 'IM', label: 'Isle of Man',                              flagCode: 'im' },
  { code: 'IN', label: 'India',                                    flagCode: 'in' },
  { code: 'IO', label: 'British Indian Ocean Territory',           flagCode: 'io' },
  { code: 'IQ', label: 'Iraq',                                     flagCode: 'iq' },
  { code: 'IR', label: 'Iran',                                     flagCode: 'ir' },
  { code: 'IS', label: 'Iceland',                                  flagCode: 'is' },
  { code: 'IT', label: 'Italy',                                    flagCode: 'it' },
  { code: 'JE', label: 'Jersey',                                   flagCode: 'je' },
  { code: 'JM', label: 'Jamaica',                                  flagCode: 'jm' },
  { code: 'JO', label: 'Jordan',                                   flagCode: 'jo' },
  { code: 'JP', label: 'Japan',                                    flagCode: 'jp' },
  { code: 'KE', label: 'Kenya',                                    flagCode: 'ke' },
  { code: 'KG', label: 'Kyrgyzstan',                               flagCode: 'kg' },
  { code: 'KH', label: 'Cambodia',                                 flagCode: 'kh' },
  { code: 'KI', label: 'Kiribati',                                 flagCode: 'ki' },
  { code: 'KM', label: 'Comoros',                                  flagCode: 'km' },
  { code: 'KN', label: 'Saint Kitts and Nevis',                    flagCode: 'kn' },
  { code: 'KP', label: 'North Korea',                              flagCode: 'kp' },
  { code: 'KR', label: 'South Korea',                              flagCode: 'kr' },
  { code: 'KW', label: 'Kuwait',                                   flagCode: 'kw' },
  { code: 'KY', label: 'Cayman Islands',                           flagCode: 'ky' },
  { code: 'KZ', label: 'Kazakhstan',                               flagCode: 'kz' },
  { code: 'LA', label: 'Laos',                                     flagCode: 'la' },
  { code: 'LB', label: 'Lebanon',                                  flagCode: 'lb' },
  { code: 'LC', label: 'Saint Lucia',                              flagCode: 'lc' },
  { code: 'LI', label: 'Liechtenstein',                            flagCode: 'li' },
  { code: 'LK', label: 'Sri Lanka',                                flagCode: 'lk' },
  { code: 'LR', label: 'Liberia',                                  flagCode: 'lr' },
  { code: 'LS', label: 'Lesotho',                                  flagCode: 'ls' },
  { code: 'LT', label: 'Lithuania',                                flagCode: 'lt' },
  { code: 'LU', label: 'Luxembourg',                               flagCode: 'lu' },
  { code: 'LV', label: 'Latvia',                                   flagCode: 'lv' },
  { code: 'LY', label: 'Libya',                                    flagCode: 'ly' },
  { code: 'MA', label: 'Morocco',                                  flagCode: 'ma' },
  { code: 'MC', label: 'Monaco',                                   flagCode: 'mc' },
  { code: 'MD', label: 'Moldova',                                  flagCode: 'md' },
  { code: 'ME', label: 'Montenegro',                               flagCode: 'me' },
  { code: 'MF', label: 'Saint Martin',                             flagCode: 'mf' },
  { code: 'MG', label: 'Madagascar',                               flagCode: 'mg' },
  { code: 'MH', label: 'Marshall Islands',                         flagCode: 'mh' },
  { code: 'MK', label: 'North Macedonia',                          flagCode: 'mk' },
  { code: 'ML', label: 'Mali',                                     flagCode: 'ml' },
  { code: 'MM', label: 'Myanmar',                                  flagCode: 'mm' },
  { code: 'MN', label: 'Mongolia',                                 flagCode: 'mn' },
  { code: 'MO', label: 'Macao',                                    flagCode: 'mo' },
  { code: 'MP', label: 'Northern Mariana Islands',                 flagCode: 'mp' },
  { code: 'MQ', label: 'Martinique',                               flagCode: 'mq' },
  { code: 'MR', label: 'Mauritania',                               flagCode: 'mr' },
  { code: 'MS', label: 'Montserrat',                               flagCode: 'ms' },
  { code: 'MT', label: 'Malta',                                    flagCode: 'mt' },
  { code: 'MU', label: 'Mauritius',                                flagCode: 'mu' },
  { code: 'MV', label: 'Maldives',                                 flagCode: 'mv' },
  { code: 'MW', label: 'Malawi',                                   flagCode: 'mw' },
  { code: 'MX', label: 'Mexico',                                   flagCode: 'mx' },
  { code: 'MY', label: 'Malaysia',                                 flagCode: 'my' },
  { code: 'MZ', label: 'Mozambique',                               flagCode: 'mz' },
  { code: 'NA', label: 'Namibia',                                  flagCode: 'na' },
  { code: 'NC', label: 'New Caledonia',                            flagCode: 'nc' },
  { code: 'NE', label: 'Niger',                                    flagCode: 'ne' },
  { code: 'NF', label: 'Norfolk Island',                           flagCode: 'nf' },
  { code: 'NG', label: 'Nigeria',                                  flagCode: 'ng' },
  { code: 'NI', label: 'Nicaragua',                                flagCode: 'ni' },
  { code: 'NL', label: 'Netherlands',                              flagCode: 'nl' },
  { code: 'NO', label: 'Norway',                                   flagCode: 'no' },
  { code: 'NP', label: 'Nepal',                                    flagCode: 'np' },
  { code: 'NR', label: 'Nauru',                                    flagCode: 'nr' },
  { code: 'NU', label: 'Niue',                                     flagCode: 'nu' },
  { code: 'NZ', label: 'New Zealand',                              flagCode: 'nz' },
  { code: 'OM', label: 'Oman',                                     flagCode: 'om' },
  { code: 'PA', label: 'Panama',                                   flagCode: 'pa' },
  { code: 'PE', label: 'Peru',                                     flagCode: 'pe' },
  { code: 'PF', label: 'French Polynesia',                         flagCode: 'pf' },
  { code: 'PG', label: 'Papua New Guinea',                         flagCode: 'pg' },
  { code: 'PH', label: 'Philippines',                              flagCode: 'ph' },
  { code: 'PK', label: 'Pakistan',                                 flagCode: 'pk' },
  { code: 'PL', label: 'Poland',                                   flagCode: 'pl' },
  { code: 'PM', label: 'Saint Pierre and Miquelon',                flagCode: 'pm' },
  { code: 'PN', label: 'Pitcairn',                                 flagCode: 'pn' },
  { code: 'PR', label: 'Puerto Rico',                              flagCode: 'pr' },
  { code: 'PS', label: 'Palestine',                                flagCode: 'ps' },
  { code: 'PT', label: 'Portugal',                                 flagCode: 'pt' },
  { code: 'PW', label: 'Palau',                                    flagCode: 'pw' },
  { code: 'PY', label: 'Paraguay',                                 flagCode: 'py' },
  { code: 'QA', label: 'Qatar',                                    flagCode: 'qa' },
  { code: 'RE', label: 'Réunion',                                  flagCode: 're' },
  { code: 'RO', label: 'Romania',                                  flagCode: 'ro' },
  { code: 'RS', label: 'Serbia',                                   flagCode: 'rs' },
  { code: 'RU', label: 'Russia',                                   flagCode: 'ru' },
  { code: 'RW', label: 'Rwanda',                                   flagCode: 'rw' },
  { code: 'SA', label: 'Saudi Arabia',                             flagCode: 'sa' },
  { code: 'SB', label: 'Solomon Islands',                          flagCode: 'sb' },
  { code: 'SC', label: 'Seychelles',                               flagCode: 'sc' },
  { code: 'SD', label: 'Sudan',                                    flagCode: 'sd' },
  { code: 'SE', label: 'Sweden',                                   flagCode: 'se' },
  { code: 'SG', label: 'Singapore',                                flagCode: 'sg' },
  { code: 'SH', label: 'Saint Helena',                             flagCode: 'sh' },
  { code: 'SI', label: 'Slovenia',                                 flagCode: 'si' },
  { code: 'SJ', label: 'Svalbard and Jan Mayen',                   flagCode: 'sj' },
  { code: 'SK', label: 'Slovakia',                                 flagCode: 'sk' },
  { code: 'SL', label: 'Sierra Leone',                             flagCode: 'sl' },
  { code: 'SM', label: 'San Marino',                               flagCode: 'sm' },
  { code: 'SN', label: 'Senegal',                                  flagCode: 'sn' },
  { code: 'SO', label: 'Somalia',                                  flagCode: 'so' },
  { code: 'SR', label: 'Suriname',                                 flagCode: 'sr' },
  { code: 'SS', label: 'South Sudan',                              flagCode: 'ss' },
  { code: 'ST', label: 'Sao Tome and Principe',                   flagCode: 'st' },
  { code: 'SV', label: 'El Salvador',                              flagCode: 'sv' },
  { code: 'SX', label: 'Sint Maarten',                             flagCode: 'sx' },
  { code: 'SY', label: 'Syria',                                    flagCode: 'sy' },
  { code: 'SZ', label: 'Eswatini',                                 flagCode: 'sz' },
  { code: 'TC', label: 'Turks and Caicos Islands',                 flagCode: 'tc' },
  { code: 'TD', label: 'Chad',                                     flagCode: 'td' },
  { code: 'TF', label: 'French Southern Territories',              flagCode: 'tf' },
  { code: 'TG', label: 'Togo',                                     flagCode: 'tg' },
  { code: 'TH', label: 'Thailand',                                 flagCode: 'th' },
  { code: 'TJ', label: 'Tajikistan',                               flagCode: 'tj' },
  { code: 'TK', label: 'Tokelau',                                  flagCode: 'tk' },
  { code: 'TL', label: 'Timor-Leste',                              flagCode: 'tl' },
  { code: 'TM', label: 'Turkmenistan',                             flagCode: 'tm' },
  { code: 'TN', label: 'Tunisia',                                  flagCode: 'tn' },
  { code: 'TO', label: 'Tonga',                                    flagCode: 'to' },
  { code: 'TR', label: 'Turkey',                                   flagCode: 'tr' },
  { code: 'TT', label: 'Trinidad and Tobago',                      flagCode: 'tt' },
  { code: 'TV', label: 'Tuvalu',                                   flagCode: 'tv' },
  { code: 'TW', label: 'Taiwan',                                   flagCode: 'tw' },
  { code: 'TZ', label: 'Tanzania',                                 flagCode: 'tz' },
  { code: 'UA', label: 'Ukraine',                                  flagCode: 'ua' },
  { code: 'UG', label: 'Uganda',                                   flagCode: 'ug' },
  { code: 'UM', label: 'US Minor Outlying Islands',                flagCode: 'um' },
  { code: 'US', label: 'United States',                            flagCode: 'us' },
  { code: 'UY', label: 'Uruguay',                                  flagCode: 'uy' },
  { code: 'UZ', label: 'Uzbekistan',                               flagCode: 'uz' },
  { code: 'VA', label: 'Vatican City',                             flagCode: 'va' },
  { code: 'VC', label: 'Saint Vincent and the Grenadines',         flagCode: 'vc' },
  { code: 'VE', label: 'Venezuela',                                flagCode: 've' },
  { code: 'VG', label: 'British Virgin Islands',                   flagCode: 'vg' },
  { code: 'VI', label: 'US Virgin Islands',                        flagCode: 'vi' },
  { code: 'VN', label: 'Vietnam',                                  flagCode: 'vn' },
  { code: 'VU', label: 'Vanuatu',                                  flagCode: 'vu' },
  { code: 'WF', label: 'Wallis and Futuna',                        flagCode: 'wf' },
  { code: 'WS', label: 'Samoa',                                    flagCode: 'ws' },
  { code: 'YE', label: 'Yemen',                                    flagCode: 'ye' },
  { code: 'YT', label: 'Mayotte',                                  flagCode: 'yt' },
  { code: 'ZA', label: 'South Africa',                             flagCode: 'za' },
  { code: 'ZM', label: 'Zambia',                                   flagCode: 'zm' },
  { code: 'ZW', label: 'Zimbabwe',                                 flagCode: 'zw' },
] as const;

export const COUNTRY_CODES: readonly string[] = COUNTRIES.map((c) => c.code);
export const COUNTRY_BY_CODE: Readonly<Record<string, Country>> = Object.freeze(
  Object.fromEntries(COUNTRIES.map((c) => [c.code, c])),
);

/** Short alias for the flag-icons CSS class lookup (jurisdiction → flagCode). */
export const FLAGS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(JURISDICTIONS.map((j) => [j.code, j.flagCode])),
);

export function isJurisdiction(code: unknown): code is string {
  return typeof code === 'string' && code in JURISDICTION_BY_CODE;
}

export function isCountry(code: unknown): code is string {
  return typeof code === 'string' && code in COUNTRY_BY_CODE;
}

export function jurisdictionFlagClass(code: string): string {
  const flag = FLAGS[code];
  return flag ? `fi fi-${flag.toLowerCase()}` : 'fi fi-xx';
}

export function countryFlagClass(code: string): string {
  const c = COUNTRY_BY_CODE[code];
  return c ? `fi fi-${c.flagCode.toLowerCase()}` : 'fi fi-xx';
}